import React, { KeyboardEventHandler, useEffect, useState } from 'react'
import { Button, LinkButton } from '@/atoms/Button'
import { UpArrowIcon } from '@/atoms/Icons/UpArrowIcon'
import { Avatar } from '@/molecules/Avatar'
import { useUser } from '@/services/UserService'
import { useWatchPartyChat } from '@/services/WatchPartyChatService'
import { useSafeTrack } from '@/utils/analytics'
import { useLoginUrl } from '@/utils/auth-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'

interface WatchPartyChatInputProps {
  startFollowing?: () => void
}

export const WatchPartyChatInput: React.FC<WatchPartyChatInputProps> = ({ startFollowing }) => {
  const { user } = useUser()
  const { t } = useTranslate('livestream')
  const { sendMessage, connected, loading, chatStatus } = useWatchPartyChat()
  const [text, setText] = useState('')
  const track = useSafeTrack()
  const { push } = useSafeRouter()
  const { loginUrl, trackLoginStarted } = useLoginUrl()
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  if (!isMounted) return null

  const handleKeypress: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') {
      handleTextSubmit()
    }
  }
  const handleTextSubmit = () => {
    sendMessage(text)
    setText('')
    startFollowing?.()
    track('Livestream Comment Sent')
  }

  const checkForAuth = async () => {
    if (!user) {
      trackLoginStarted('watch-party-input-click-event')
      push(loginUrl)
    }
  }
  const disabled = chatStatus !== 'online' || !user || !connected

  if (connected && !user) {
    return (
      <LinkButton
        href={loginUrl}
        variant="white"
        outline
        className="mx-2 justify-center"
        onClick={() => trackLoginStarted('watch-party-input-login-button')}
      >
        {t('loginToSendAMessage', 'Login to send a message')}
      </LinkButton>
    )
  }

  const canSend = Boolean(!disabled && text)

  let placeholder

  if (loading) {
    placeholder = t('loading', 'Loading...')
  } else if (chatStatus === 'offline') {
    placeholder = t('sendingMessagesDisabled', 'Sending messages is currently disabled')
  } else if (user?.isAdmin) {
    placeholder = t('postingAsAdmin', 'Posting as Admin')
  } else {
    placeholder = t('typeYourMessage', 'Type your message')
  }

  return (
    <div className={'px-2'} onClick={checkForAuth}>
      <div className="flex grow-1 flex-row items-center rounded-lg bg-core-gray-950 p-2 text-xs">
        <div className="mr-2">
          <Avatar />
        </div>
        <input
          className="flex grow bg-core-gray-950 py-2 outline-none focus-visible:!shadow-none focus-visible:!ring-transparent focus-visible:!ring-offset-transparent"
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => setText(e.target.value)}
          value={text}
          onKeyDown={handleKeypress}
        />
        {canSend && (
          <div className="send">
            <Button
              variant="gray-900"
              className="h-8 w-8 rounded-full p-0"
              disabled={!canSend}
              aria-label={t('sendMessage', 'Send Message')}
              onClick={handleTextSubmit}
            >
              <UpArrowIcon size={32} color={!canSend ? 'black' : 'white'} />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
